import { InputProps, useBreakpointValue } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ChargePeriod, TariffType, TOURateType } from 'clipsal-cortex-types/src/api/api-tariffs-v2';

import { COMPONENT_MIN_HEIGHT, TOP_NAV_SPACING_AFFORDANCE } from '../../common/constants';
import { selectSite } from '../site/siteSlice';
import { getDefaultFlatTariffRates } from './flat-tariff/flat-tariff-helpers';
import { getDefaultEffectiveDate } from './tariff-effective-date';
import { useTranslatedEmptySeasonsTemplate } from './tariff-season/tariff-season-helpers';
import { TariffData, TariffRatesPerType, TariffTypeConfig } from './tariff-types';
import { useTariffs } from './tariffApi';
import { getDefaultTieredTariffRates } from './tiered-tariff/tiered-tariff-helpers';
import { TOUTariffFormKey } from './tou-tariff/tou-tariff-constants';
import { getDefaultTOUTariffRates } from './tou-tariff/tou-tariff-helpers';

export const TARIFF_TYPE_CONFIG: TariffTypeConfig[] = [
  {
    type: 'FLAT',
  },
  {
    type: 'TIERED',
  },
  {
    type: 'TOU',
  },
];

// This hook helps set up sensible default form values when setting up a new tariff.
export const useTranslatedDefaultTariffData = (): TariffData => {
  const site = useSelector(selectSite);
  const { tariffs } = useTariffs();
  const EMPTY_SEASONS_TEMPLATE = useTranslatedEmptySeasonsTemplate();
  const DEFAULT_TARIFF_DATA = {
    effectiveDate: getDefaultEffectiveDate(tariffs, site.timezone),
    tariff: {
      tariffType: 'FLAT' as TariffType,
      planName: '',
      rates: [getDefaultFlatTariffRates(1), getDefaultFlatTariffRates(2)],
      seasons: EMPTY_SEASONS_TEMPLATE,
      retailer: {
        id: 0,
        name: '',
      },
      utility: {
        id: null,
        name: '',
        state: '',
      },
      distributorId: 0,
      holidayCountry: '',
      holidaySubdiv: '',
    },
    zipCode: site.post_code,
  };

  return DEFAULT_TARIFF_DATA;
};

export const TOU_RATE_TYPE_TO_FORM_TERM_MAP: Record<TOURateType, TOUTariffFormKey> = {
  PEAK: 'peak',
  OFF_PEAK: 'offPeak',
  PARTIAL_PEAK: 'partialPeak',
  SINGLE: 'peak', // not yet known how to handle single rate type
};

export const TARIFF_TO_DEFAULT_FUNCTION_MAP: Record<TariffType, (seasonIndex: number) => TariffRatesPerType> = {
  FLAT: getDefaultFlatTariffRates,
  TIERED: getDefaultTieredTariffRates,
  TOU: getDefaultTOUTariffRates,
  REAL_TIME: () => {
    throw new Error('SE Home does not support real-time rates!');
  },
};

export const CHARGE_PERIOD_TO_LABEL_MAP: Record<ChargePeriod, string> = {
  MONTHLY: 'Monthly',
  DAILY: 'Daily',
  QUARTERLY: 'Quarterly',
  ANNUALLY: 'Annually',
};

export const useTranslatedCommonInputProps = (): InputProps => {
  const { t } = useTranslation();
  return {
    borderRadius: '1px',
    borderColor: '#9FA0A4',
    py: 6,
    _dark: { background: '#27282A' },
    size: 'md',
    placeholder: `${t('Energy Rates.enter price per kwh in usd')}...`,
    _placeholder: { color: '#929497' },
    type: 'number',
    min: 0,
  };
};

// Responsive sizing shared by the Highcharts tooltips on the tariff confirm pages (TOU, flat, tiered).
export const useTariffChartTooltipSizes = () => {
  const fontSize = useBreakpointValue({ base: 12, md: 16 });
  const markerSize = useBreakpointValue({ base: 14, md: 24 });
  const padding = useBreakpointValue({ base: 8, md: 12 });
  const borderRadius = useBreakpointValue({ base: 12, md: 24 });

  return {
    fontSize: fontSize ? `${fontSize}px` : undefined,
    markerSize,
    padding,
    borderRadius,
  };
};

export const MIN_TARIFF_PAGE_HEIGHT = `calc(${COMPONENT_MIN_HEIGHT} - ${TOP_NAV_SPACING_AFFORDANCE} - 10vh)`;

export const MAX_TARIFF_PAGE_WIDTH = `calc(100vw - env(safe-area-inset-left) - env(safe-area-inset-right))`;
